import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { motion, AnimatePresence } from 'motion/react';
import { Slider, Unit } from '@frontend/components';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { SettingsTenantLocation } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  DropdownField,
  Heading,
  IconButton,
  InfoRoundIconSmall,
  Modal,
  ModalControlModalProps,
  RadioCardField,
  Table,
  Text,
  useModalControl,
  PrimaryButton,
  EditSimpleIcon,
  XIcon,
  Chip,
  ChecklistMenuField,
  CheckboxField,
  TextLink,
} from '@frontend/design-system';
import { useCallQueueCacheUpdater } from '../../../hooks/useCallQueueCacheUpdater';
import { trackingId } from '../../../tracking';
import { AudioPicker } from '../../audio-picker/audio-picker';
import { SettingsCard } from '../../common/settings-card';
import { WeaveHelpLink } from '../../weave-support-link';
import { AssignDeviceModal, useAssignDeviceAPI } from './assign-device';
import { FIRST_ANNOUNCEMENT_DELAY_OPTIONS, REPEAT_INTERVAL_OPTIONS, SchemaForm } from './schema';
import { CallQueueState, CallQueueDevice, CallQueueRingType } from './types';

export const CallQueuePage = ({
  data,
  tenantLocation,
  form,
  onSave,
}: {
  data: CallQueueState;
  tenantLocation?: SettingsTenantLocation;
  form: SchemaForm;
  onSave?: () => Promise<void>;
}) => {
  const { t } = useTranslation('phone');

  if (!tenantLocation) {
    return (
      <SettingsCard title=''>
        <div>{t('No Phone Tenant Selected')}</div>
      </SettingsCard>
    );
  }

  return (
    <CallQueueContent key={data.callQueueId} data={data} tenantLocation={tenantLocation} form={form} onSave={onSave} />
  );
};

const CallQueueContent = ({
  data,
  tenantLocation,
  form,
  onSave,
}: {
  data: CallQueueState;
  tenantLocation: SettingsTenantLocation;
  form: SchemaForm;
  onSave?: () => Promise<void>;
}) => {
  const { t } = useTranslation('phone');
  const [callQueue, setCallQueue] = useState<CallQueueState>(() => data);

  const tenantId = tenantLocation.phoneTenantId;
  const isUnify = tenantLocation.locationType === 'unify';

  const locationOptions = useMemo(() => {
    return (
      tenantLocation.children?.map((location) => ({
        label: location.name ?? '',
        value: location.locationId ?? '',
      })) ?? []
    );
  }, [tenantLocation]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
      <AssignDeviceCard
        callQueueId={callQueue.callQueueId}
        assignedDevices={callQueue.devices}
        setState={setCallQueue}
        tenantLocation={tenantLocation}
      />
      <RoutingCard form={form} />
      <CallerExperienceCard form={form} tenantId={tenantId ?? ''} />
      {isUnify && locationOptions.length > 0 ? <LocationsCard form={form} options={locationOptions} /> : null}
      <PrimaryButton
        size='large'
        css={{ width: 'fit-content' }}
        onClick={onSave}
        disabled={!form.changedValues}
        trackingId={trackingId({ context: 'setting', feature: 'call-queues', details: 'save' })}
      >
        {t('Save Changes')}
      </PrimaryButton>
    </div>
  );
};

export const AssignDeviceTableWithModal = ({
  callQueueId,
  assignedDevices,
  tenantLocation,
  setState,
  tableLayout = 'separated',
  modalControl,
}: {
  callQueueId: string;
  assignedDevices: CallQueueDevice[];
  tenantLocation: SettingsTenantLocation;
  setState?: Dispatch<SetStateAction<CallQueueState>>;
  tableLayout?: 'combined' | 'separated';
  modalControl?: {
    modalProps: any;
    openModal: () => void;
    setPreSelectedDevices?: (devices: CallQueueDevice[]) => void;
  };
}) => {
  const { t } = useTranslation('phone');
  const defaultModalControl = useModalControl();
  const { modalProps, openModal } = modalControl || defaultModalControl;
  const { navigate } = useSettingsNavigate();
  const { mutation } = useAssignDeviceAPI(tenantLocation);
  const { getScopeName } = useAppScopeStore();
  const isUnify = tenantLocation && tenantLocation.locationType === 'unify';
  const [preSelectedDevices, setPreSelectedDevices] = useState<CallQueueDevice[]>(assignedDevices);

  const updateDevices = (devices: CallQueueDevice[]) => {
    if (setState) {
      setState((state) => ({ ...state, devices: devices }));
    }
  };

  const { updateCallRouteCaches } = useCallQueueCacheUpdater(callQueueId);

  const sortedAssignedDevices = useMemo(() => {
    return [...assignedDevices].sort((a, b) => a.name.localeCompare(b.name));
  }, [assignedDevices]);

  const removeAction = [
    {
      label: t('Remove Device'),
      Icon: tableLayout === 'separated' ? XIcon : () => <Icon name='remove2' color='subdued' />,
      onClick: (data: CallQueueDevice) => {
        const remainingDevices = assignedDevices.filter((device) => device.sipProfileId !== data.sipProfileId);
        const deviceNames = remainingDevices?.map((device) => device.name).filter(Boolean) || [];

        mutation.mutate(
          {
            callQueueId,
            devices: remainingDevices,
          },
          {
            onSuccess: (_data, payload) => {
              updateDevices(payload.devices);
              updateCallRouteCaches({
                deviceNames,
                deviceCount: deviceNames.length,
              });
            },
          }
        );
      },
      trackingId: trackingId({ context: 'setting', feature: 'call-queues', details: 'remove-device' }),
    },
  ];

  const deviceActions = [
    {
      label: t('Edit Device'),
      Icon: EditSimpleIcon,
      onClick: (data: CallQueueDevice) => {
        if (data.deviceId) {
          navigate({ to: '/phone/devices/:id', params: { id: data.deviceId } });
        }
      },
      trackingId: trackingId({ context: 'setting', feature: 'call-queues', details: 'edit-device' }),
    },
    ...removeAction,
  ];

  return (
    <>
      {tableLayout === 'combined' && (
        <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Heading level={3}>{t('Assigned Devices')}</Heading>
          <TextLink
            css={{ fontWeight: 'bold' }}
            onClick={() => {
              setPreSelectedDevices(assignedDevices);
              openModal();
            }}
          >
            {t('Assign Devices')}
          </TextLink>
        </div>
      )}
      <Table
        isPaginated
        fullHeight
        fullHeightConfig={{
          minHeight: 200,
        }}
        data={sortedAssignedDevices}
        colConfig={[
          { id: 'name', accessor: 'name' as const, Header: t('Assigned Device'), sortType: 'string' },
          ...(tableLayout === 'combined'
            ? []
            : [{ id: 'macAddress', accessor: 'macAddress' as const, Header: t('MAC Address') }]),
          {
            id: 'labels',
            Header: t('Locations'),
            accessor: (data) => {
              const locationIds = data.locationIds;
              if (!locationIds) {
                return { label: '--', type: 'none' };
              }
              if (locationIds.length === 1) {
                const scopeName = getScopeName(locationIds[0]);
                if (!scopeName) {
                  return { label: '--', type: 'none' };
                }
                return { label: scopeName, type: 'single' };
              } else if (locationIds.length > 1) {
                return { label: t('{{count}} locations', { count: locationIds.length }), type: 'multi' };
              } else {
                return { label: '--', type: 'none' };
              }
            },
            cellRenderer: (data: { label: string; type: 'multi' | 'single' | 'none' }) => {
              if (data.type === 'none') {
                return '--';
              }
              if (data.type === 'multi') {
                return <Chip.MultiChip>{data.label}</Chip.MultiChip>;
              }
              return <Chip.SingleChip>{data.label}</Chip.SingleChip>;
            },
            width: 150,
            omit: !isUnify,
            sortType: (a, b) => {
              return a.values.labels.label.localeCompare(b.values.labels.label);
            },
          },
        ]}
        rowActions={{
          actions: tableLayout === 'combined' ? removeAction : deviceActions,
        }}
        emptyStateConfig={{
          type: 'sync_your_phone',
          header: t('No Assigned Devices'),
          description: () => (
            <Trans t={t}>
              <Text textAlign='center'>You don't have any devices assigned yet. </Text>
              <Text textAlign='center'>
                Click{' '}
                <Text as='span' weight='bold'>
                  Assign Device
                </Text>{' '}
                to get started.
              </Text>
            </Trans>
          ),
        }}
      />
      <AssignDeviceModal
        callQueueId={callQueueId}
        modalProps={modalProps}
        assignedDevices={assignedDevices}
        preSelectedDevices={preSelectedDevices}
        setPreSelectedDevices={setPreSelectedDevices}
        updateDevices={updateDevices}
        tenantLocation={tenantLocation}
      />
    </>
  );
};

export const AssignDeviceCard = ({
  callQueueId,
  assignedDevices,
  tenantLocation,
  setState,
}: {
  callQueueId: string;
  assignedDevices: CallQueueDevice[];
  tenantLocation: SettingsTenantLocation;
  setState: Dispatch<SetStateAction<CallQueueState>>;
}) => {
  const { t } = useTranslation('phone');
  const modalControl = useModalControl();
  const { openModal, triggerProps } = modalControl;

  return (
    <SettingsCard
      title={t('Assigned Devices')}
      action={{
        label: t('Assign Device'),
        onClick: openModal,
        ref: triggerProps.ref,
        trackingId: trackingId({ context: 'setting', feature: 'call-queues', details: 'assign-devices' }),
      }}
    >
      <AssignDeviceTableWithModal
        callQueueId={callQueueId}
        assignedDevices={assignedDevices}
        tenantLocation={tenantLocation}
        setState={setState}
        modalControl={modalControl}
      />
    </SettingsCard>
  );
};

export const RoutingOptions = ({
  form,
  condensed,
  tableLayout,
}: {
  form: SchemaForm;
  condensed?: boolean;
  tableLayout?: 'combined' | 'separated';
}) => {
  const { t } = useTranslation('phone');

  return (
    <SettingsSection css={{ width: 'auto' }}>
      {tableLayout === 'combined' ? (
        <Heading level={3}>{t('Routing')}</Heading>
      ) : (
        <SectionHeading condensed={condensed}>{t('Routing Options')}</SectionHeading>
      )}
      {tableLayout === 'combined' ? (
        <DropdownField
          {...form.getFieldProps('deviceRingType')}
          name='deviceRingType'
          label={t('Choose Routing Option')}
          helperText={
            form.getFieldProps('deviceRingType').value === CallQueueRingType.RING_TYPE_RING_ALL
              ? t('All available devices will ring at the same time.')
              : form.getFieldProps('deviceRingType').value === CallQueueRingType.RING_TYPE_LONGEST_IDLE_AGENT
              ? t("Ring the device that's been idle for the longest time.")
              : ''
          }
          css={{ maxWidth: 320 }}
        >
          <DropdownField.Option value={CallQueueRingType.RING_TYPE_RING_ALL}>{t('Simultaneous')}</DropdownField.Option>
          <DropdownField.Option value={CallQueueRingType.RING_TYPE_LONGEST_IDLE_AGENT}>
            {t('Longest Idle')}
          </DropdownField.Option>
        </DropdownField>
      ) : (
        <RadioCardField {...form.getFieldProps('deviceRingType')} name='deviceRingType'>
          <RadioCardField.Option
            css={{ maxWidth: condensed ? '100%' : 240 }}
            value={CallQueueRingType.RING_TYPE_RING_ALL}
            title={t('Simultaneous')}
            description={t('All available devices will ring at the same time.')}
          />
          <RadioCardField.Option
            css={{ maxWidth: condensed ? '100%' : 240 }}
            value={CallQueueRingType.RING_TYPE_LONGEST_IDLE_AGENT}
            title={t('Longest Idle')}
            description={t("Ring the device that's been idle for the longest time.")}
          />
        </RadioCardField>
      )}
    </SettingsSection>
  );
};

export const RoutingCard = ({ form, condensed }: { form: SchemaForm; condensed?: boolean }) => {
  const { t } = useTranslation('phone');

  return (
    <SettingsCard title={t('Routing')} condensed={condensed}>
      <RoutingOptions form={form} condensed={condensed} />
    </SettingsCard>
  );
};

const checkboxDiv = css`
  display: grid;
  grid-template-areas: 'checkbox-row checkbox-row checkbox-row' '. description description';
  grid-template-columns: 0.2fr 1fr 1fr;
`;
const checkboxRow = css`
  grid-area: checkbox-row;
`;
const description = css`
  grid-area: description;
`;

export const CallerExperienceOptions = ({
  form,
  tenantId,
  condensed,
  tableLayout,
}: {
  form: SchemaForm;
  tenantId: string;
  condensed?: boolean;
  tableLayout?: 'combined' | 'separated';
}) => {
  const { t } = useTranslation('phone');
  const { modalProps, openModal } = useModalControl();

  const greetingField = form.getFieldProps('greetingEnabled');
  const greetingMedia = form.getFieldProps('greetingMediaId');
  const escapeField = form.getFieldProps('allowExit');
  const holdMusicMedia = form.getFieldProps('holdMusicId');
  const holdMusicSystemMedia = form.getFieldProps('holdMusicSystemMedia');
  const positionAnnouncementField = form.getFieldProps('positionAnnouncementEnabled');
  const initialAnnouncementField = form.getFieldProps('firstAnnouncementDelayMs');
  const announcementIntervalField = form.getFieldProps('repeatIntervalMs');
  const maxHoldTime = form.getFieldProps('maxWaitTimeMs');
  const sliderValue = useMemo(() => [parseInt(maxHoldTime.value)], [maxHoldTime.value]);
  const SECTION_WIDTH = condensed ? 310 : 320;
  const SCRUBBER_WIDTH = condensed ? 205 : 250;

  return (
    <div>
      {tableLayout === 'combined' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          <Heading level={3}>{t('Caller Experience')}</Heading>
          <SettingsSectionCombinedLayout>
            <div>
              <CheckboxField
                {...greetingField}
                name='greetingEnabled'
                label={
                  <Text>
                    <div style={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center' }}>
                      {t('Add Greeting')}
                      <Icon
                        name='info'
                        css={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          openModal();
                        }}
                      />
                      <GreetingHelpModal modalProps={modalProps} />
                    </div>
                  </Text>
                }
                labelPlacement='right'
              />
              <Text size='medium' css={{ paddingLeft: theme.spacing(3.5) }}>
                {t('Add an optional greeting to play before the caller enters the queue.')}
              </Text>
            </div>
            <AnimatePresence>
              {greetingField.value && (
                <motion.div variants={variants} animate='open' initial='closed' exit='closed'>
                  <AudioPicker
                    widths={{ field: SECTION_WIDTH, scrubber: SCRUBBER_WIDTH }}
                    allowedOptions={{
                      add: true,
                      custom: true,
                      standard: false,
                    }}
                    field={greetingMedia}
                    name='greetingMediaId'
                    labels={{
                      placeholder: t('Select Greeting'),
                    }}
                    tenantId={tenantId}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </SettingsSectionCombinedLayout>

          <SettingsSectionCombinedLayout>
            <div>
              <CheckboxField
                {...escapeField}
                name='allowExit'
                label={<Text>{t('Add Escape Option')}</Text>}
                labelPlacement='right'
              />
              <Text size='medium' css={{ paddingLeft: theme.spacing(3.5) }}>
                {t('Allow callers to press zero to escape the queue and go to the next step in your call routing.')}
              </Text>
            </div>
          </SettingsSectionCombinedLayout>

          <SettingsSectionCombinedLayout css={{ width: 'auto' }}>
            <div style={{ width: SECTION_WIDTH }}>
              <Text size='medium' weight='bold'>
                {t('Hold Music')}
              </Text>
            </div>
            <AudioPicker
              widths={{ field: SECTION_WIDTH, scrubber: SCRUBBER_WIDTH }}
              allowedOptions={{ add: true, custom: true, standard: true }}
              field={holdMusicMedia}
              name='holdMusicId'
              labels={{ placeholder: t('Select Hold Music') }}
              tenantId={tenantId}
              hideTTS={true}
            />
            <input hidden type='checkbox' readOnly checked={holdMusicSystemMedia.value} />
          </SettingsSectionCombinedLayout>

          <SettingsSectionCombinedLayout>
            <div>
              <CheckboxField
                {...positionAnnouncementField}
                name='positionAnnouncementEnabled'
                label={<Text>{t('Add Position Announcement')}</Text>}
                labelPlacement='right'
              />
              <Text size='medium' css={{ paddingLeft: theme.spacing(3.5) }}>
                {t('Play a message that informs the caller of their position in the call queue.')}
              </Text>
            </div>
            <AnimatePresence>
              {positionAnnouncementField.value && (
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(1),
                    paddingTop: theme.spacing(2),
                  }}
                >
                  <Text size='medium' weight='bold'>
                    {t('Position Announcement')}
                  </Text>
                  <motion.div
                    variants={variants}
                    animate='open'
                    initial='closed'
                    exit='closed'
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: theme.spacing(2),
                      width: '100%',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <DropdownField
                        {...initialAnnouncementField}
                        name='firstAnnouncementDelayMs'
                        label={t('Initial Announcement')}
                      >
                        {FIRST_ANNOUNCEMENT_DELAY_OPTIONS.map((option) => (
                          <DropdownField.Option key={option.value} value={option.value}>
                            {option.label}
                          </DropdownField.Option>
                        ))}
                      </DropdownField>
                    </div>

                    <div style={{ flex: 1 }}>
                      <DropdownField
                        {...announcementIntervalField}
                        name='repeatIntervalMs'
                        label={t('Repeating every')}
                      >
                        {REPEAT_INTERVAL_OPTIONS.map((option) => (
                          <DropdownField.Option key={option.value} value={option.value}>
                            {option.label}
                          </DropdownField.Option>
                        ))}
                      </DropdownField>
                    </div>
                  </motion.div>
                </div>
              )}
            </AnimatePresence>
          </SettingsSectionCombinedLayout>

          <SettingsSection>
            <div>
              <SectionHeading condensed={condensed}>{t('Maximum Caller Hold Time')}</SectionHeading>
            </div>
            <div style={{ padding: theme.spacing(2), width: 224 }}>
              <Slider
                max={60}
                min={1}
                onChange={(values) => {
                  maxHoldTime.onChange({ name: 'maxWaitTimeMs', value: values[0].toString() });
                }}
                step={1}
                values={sliderValue}
                units={Unit.Minutes}
              />
            </div>
          </SettingsSection>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <SettingsSection>
            <div css={checkboxDiv}>
              <CheckboxField
                {...greetingField}
                css={checkboxRow}
                name='greetingEnabled'
                label={
                  <SectionHeading condensed={condensed}>
                    <div style={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center' }}>
                      {t('Add Greeting')}
                      <IconButton
                        size='xsmall'
                        label={t('Greeting Help')}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          openModal();
                        }}
                      >
                        <InfoRoundIconSmall />
                      </IconButton>
                      <GreetingHelpModal modalProps={modalProps} />
                    </div>
                  </SectionHeading>
                }
                labelPlacement='right'
              />
              <Text size='medium' color='light' css={description}>
                {t('Add an optional greeting to play before the caller enters the queue.')}
              </Text>
            </div>
            <AnimatePresence>
              {greetingField.value && (
                <motion.div variants={variants} animate='open' initial='closed' exit='closed'>
                  <AudioPicker
                    widths={{ field: SECTION_WIDTH, scrubber: SCRUBBER_WIDTH }}
                    allowedOptions={{
                      add: true,
                      custom: true,
                      standard: false,
                    }}
                    field={greetingMedia}
                    name='greetingMediaId'
                    labels={{
                      placeholder: t('Select Greeting'),
                    }}
                    tenantId={tenantId}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </SettingsSection>

          <SettingsSection>
            <div css={checkboxDiv}>
              <CheckboxField
                {...escapeField}
                css={checkboxRow}
                name='allowExit'
                label={<SectionHeading condensed={condensed}>{t('Add Escape Option')}</SectionHeading>}
                labelPlacement='right'
              />
              <Text size='medium' color='light' css={description}>
                {t('Allow callers to press zero to escape the queue and go to the next step in your call routing.')}
              </Text>
            </div>
          </SettingsSection>

          <SettingsSection css={{ width: 'auto' }}>
            <div style={{ width: SECTION_WIDTH }}>
              <SectionHeading condensed={condensed}>{t('Hold Music')}</SectionHeading>
              <Text size='medium' color='light'>
                {t('Play an audio file while callers wait for their call to be answered.')}
              </Text>
            </div>
            <AudioPicker
              widths={{ field: SECTION_WIDTH, scrubber: SCRUBBER_WIDTH }}
              allowedOptions={{ add: true, custom: true, standard: true }}
              field={holdMusicMedia}
              name='holdMusicId'
              labels={{ placeholder: t('Select Hold Music') }}
              tenantId={tenantId}
              hideTTS={true}
            />
            <input hidden type='checkbox' readOnly checked={holdMusicSystemMedia.value} />
          </SettingsSection>

          <SettingsSection>
            <div css={checkboxDiv}>
              <CheckboxField
                {...positionAnnouncementField}
                css={checkboxRow}
                name='positionAnnouncementEnabled'
                label={<SectionHeading condensed={condensed}>{t('Add Position Announcement')}</SectionHeading>}
                labelPlacement='right'
              />
              <Text size='medium' color='light' css={description}>
                {t('Play a message that informs the caller of their position in the call queue.')}
              </Text>
            </div>
            <AnimatePresence>
              {positionAnnouncementField.value && (
                <motion.div
                  variants={variants}
                  animate='open'
                  initial='closed'
                  exit='closed'
                  style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}
                >
                  <DropdownField
                    {...initialAnnouncementField}
                    name='firstAnnouncementDelayMs'
                    label={t('Initial Announcement')}
                  >
                    {FIRST_ANNOUNCEMENT_DELAY_OPTIONS.map((option) => (
                      <DropdownField.Option key={option.value} value={option.value}>
                        {option.label}
                      </DropdownField.Option>
                    ))}
                  </DropdownField>
                  <DropdownField {...announcementIntervalField} name='repeatIntervalMs' label={t('Repeating every')}>
                    {REPEAT_INTERVAL_OPTIONS.map((option) => (
                      <DropdownField.Option key={option.value} value={option.value}>
                        {option.label}
                      </DropdownField.Option>
                    ))}
                  </DropdownField>
                </motion.div>
              )}
            </AnimatePresence>
          </SettingsSection>

          <SettingsSection>
            <div>
              <SectionHeading condensed={condensed}>{t('Maximum Caller Hold Time')}</SectionHeading>
              <Text size='medium' color='light'>
                {t('The maximum time the caller will be in the call queue.')}
              </Text>
            </div>
            <div style={{ padding: theme.spacing(2) }}>
              <Slider
                max={60}
                min={1}
                onChange={(values) => {
                  maxHoldTime.onChange({ name: 'maxWaitTimeMs', value: values[0].toString() });
                }}
                step={1}
                values={sliderValue}
                units={Unit.Minutes}
              />
            </div>
          </SettingsSection>
        </div>
      )}

      <GreetingHelpModal modalProps={modalProps} />
    </div>
  );
};

export const CallerExperienceCard = ({
  form,
  tenantId,
  condensed,
}: {
  form: SchemaForm;
  tenantId: string;
  condensed?: boolean;
}) => {
  const { t } = useTranslation('phone');

  return (
    <SettingsCard title={t('Caller Experience')} condensed={condensed}>
      <CallerExperienceOptions form={form} tenantId={tenantId} condensed={condensed} />
    </SettingsCard>
  );
};

const SettingsSection = ({ children, className }: { children: ReactNode; className?: string }) => {
  return (
    <section
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        width: 320,
      }}
      className={className}
    >
      {children}
    </section>
  );
};

const SettingsSectionCombinedLayout = ({ children, className }: { children: ReactNode; className?: string }) => {
  return (
    <section
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
      }}
      className={className}
    >
      {children}
    </section>
  );
};

const SectionHeading = ({ condensed, children }: { children: ReactNode; condensed?: boolean }) => {
  return condensed ? <Text weight='bold'>{children}</Text> : <Heading level={3}>{children}</Heading>;
};

const variants = {
  open: {
    height: 'auto',
    opacity: 1,
    transition: {
      ease: 'easeInOut',
      opacity: { duration: 0.3 },
      height: { duration: 0.1 },
    },
  },
  closed: {
    height: 0,
    opacity: 0,
    transition: {
      ease: 'easeInOut',
      opacity: { duration: 0.15 },
      height: { duration: 0.3 },
    },
  },
};

const GreetingHelpModal = ({ modalProps }: { modalProps: ModalControlModalProps }) => {
  const { t } = useTranslation('phone');

  return (
    <Modal maxWidth={600} {...modalProps}>
      <Modal.Header onClose={modalProps.onClose}>{t('Greeting Help')}</Modal.Header>
      <Modal.Body style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        <Trans t={t}>
          <Text>This optional audio file will be the first thing the caller hears before entering the queue.</Text>
          <Text>
            The greeting is often used to welcome your callers, show them appreciation for calling, and inform them of
            any escape options they may choose while in the queue.
          </Text>
          <Text>At this time, the only escape option will be 0.</Text>
          <div>
            <Text weight='bold'>{t('Example')}</Text>

            <Text>
              "Thank you for calling our dental office. We appreciate your call and will be with you shortly. To escape
              the queue, please press 0."
            </Text>
          </div>
          <Text>
            For more help on uploading a call queue greeting, visit <WeaveHelpLink />.
          </Text>
        </Trans>
      </Modal.Body>
      <Modal.Actions primaryLabel={t('Done')} onPrimaryClick={modalProps.onClose} />
    </Modal>
  );
};

export const LocationsOptions = ({
  form,
  options,
  tableLayout,
}: {
  form: SchemaForm;
  options: { label: string; value: string }[];
  tableLayout?: 'combined' | 'separated';
}) => {
  const { t } = useTranslation('phone');

  return tableLayout === 'combined' ? (
    <SettingsSectionCombinedLayout>
      <div data-tracking-id={trackingId({ context: 'setting', feature: 'call-queues', details: 'location-select' })}>
        <ChecklistMenuField
          {...form.getFieldProps('locationIds')}
          label={t('Locations')}
          name='locationIds'
          helperText={t('Selecting none will auto-label all locations to this Call Queue.')}
        >
          {options.map((option) => (
            <ChecklistMenuField.Option key={option.value} value={option.value}>
              {option.label}
            </ChecklistMenuField.Option>
          ))}
        </ChecklistMenuField>
      </div>
    </SettingsSectionCombinedLayout>
  ) : (
    <SettingsSection>
      <div data-tracking-id={trackingId({ context: 'setting', feature: 'call-queues', details: 'location-select' })}>
        <ChecklistMenuField
          {...form.getFieldProps('locationIds')}
          label={t('Locations')}
          name='locationIds'
          helperText={t('Selecting none will auto-label all locations to this Call Queue.')}
        >
          {options.map((option) => (
            <ChecklistMenuField.Option key={option.value} value={option.value}>
              {option.label}
            </ChecklistMenuField.Option>
          ))}
        </ChecklistMenuField>
      </div>
    </SettingsSection>
  );
};

export const LocationsCard = ({ form, options }: { form: SchemaForm; options: { label: string; value: string }[] }) => {
  const { t } = useTranslation('phone');

  return (
    <SettingsCard
      title={t('Locations (Optional)')}
      description={t("Select the locations whose users can access this queue's real-time data.")}
      condensed
    >
      <LocationsOptions form={form} options={options} />
    </SettingsCard>
  );
};
